import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

// Session timeout configuration
const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
const WARNING_TIME = 10 * 1000; // 10 seconds before timeout

/**
 * Custom hook to handle session timeout after inactivity
 * - Tracks user activity (mouse, keyboard, clicks, scrolls, touch)
 * - Automatically logs out after 5 minutes of inactivity
 * Note: The countdown display is handled by SessionCounter component in the header
 */
const useSessionTimeout = () => {
  const { isAuthenticated, logout } = useAuth();
  
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Handle logout
  const handleLogout = useCallback(() => {
    // Logout user
    logout();
  }, [logout]);

  // Reset the session timeout timer
  const resetTimeout = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Only set timeout if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    // Update last activity time
    lastActivityRef.current = Date.now();

    // Set logout timeout (5 minutes)
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, SESSION_TIMEOUT);
  }, [isAuthenticated, handleLogout]);

  // Track user activity
  const handleActivity = useCallback(() => {
    resetTimeout();
  }, [resetTimeout]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timeout if user is not authenticated
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Initialize timeout on mount
    resetTimeout();

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, resetTimeout, handleActivity]);
};

export default useSessionTimeout;
